import { Alert, Box, Typography, Chip } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const TimesheetOfflineBanner = ({ context = 'detail' }) => {
  const isOnline = useSelector((state) => state.pwa.isOnline);
  const lastOnlineTime = useSelector((state) => state.pwa.lastOnlineTime);
  const isSyncing = useSelector((state) => state.timesheetOffline.isSyncing);
  const offlineQueueCount = useSelector((state) => state.timesheetOffline.offlineQueueCount);
  const offlineDraftSaved = useSelector((state) => state.timesheetOffline.offlineDraftSaved);
  const lastSyncedCount = useSelector((state) => state.timesheetOffline.lastSyncedCount);

  // Show sync complete message briefly
  if (isOnline && lastSyncedCount > 0 && !isSyncing) {
    return (
      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
        {lastSyncedCount} timesheet{lastSyncedCount > 1 ? 's' : ''} synced successfully!
      </Alert>
    );
  }

  // Show syncing state
  if (isSyncing) {
    return (
      <Alert severity="info" icon={<CloudSyncIcon />} sx={{ mb: 2 }}>
        Syncing {offlineQueueCount} queued submission{offlineQueueCount > 1 ? 's' : ''}...
      </Alert>
    );
  }

  // Show queued submissions when online
  if (isOnline && offlineQueueCount > 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {offlineQueueCount} timesheet submission{offlineQueueCount > 1 ? 's' : ''} waiting to sync.
      </Alert>
    );
  }

  if (!isOnline) {
    return (
      <Alert severity="warning" icon={<WifiOffIcon />} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            You're offline
          </Typography>
          <Typography variant="caption">
            {context === 'detail'
              ? 'Changes are auto-saved locally. Submissions will queue and send when you reconnect.'
              : `Viewing cached data from ${dayjs(lastOnlineTime).format('DD MMM YYYY HH:mm')}`}
          </Typography>
          {offlineDraftSaved && context === 'detail' && (
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={`Draft saved ${dayjs(offlineDraftSaved.savedAt).format('HH:mm:ss')}`}
                size="small"
                color="success"
                sx={{ fontSize: '0.65rem' }}
              />
            </Box>
          )}
        </Box>
      </Alert>
    );
  }

  return null;
};

export default TimesheetOfflineBanner;
